/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { createDefaultGridModel, ROW_DIRTY_CLASS } from '@gh/common-internal-shared';
import { getRouteInitData, whenModified } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_ROW_LAYOUT } from '@gh/core-ui';
import { buildEditableAutocompleteColumn, GridDefinition, GridDefinitionFactory, GridLayout } from '@gh/core-ui-grid';
import {
  getReportRohcAreaId,
  getReportRohcOfficeCatalogId,
  getReportRohcOfficeId,
  ReportRohcArea,
  ReportRohcOffice,
  reportRohcOfficeFieldState,
  validateReportRohcOfficeField,
} from '../../../resources/report-roch-details.model';
import { ReportRohcDetailsBusinessService } from '../../../resources/report-rohc-details-business.service';
import { ReportRohcDetailsCommandService } from '../report-rohc-details-command.service';
import { ReportRohcDetailsFormState } from '../report-rohc-details-form.service';

@Component({
  selector: 'gh-report-rohc-area-management',
  template: require('./report-rohc-area-management.component.html'),
  styles: [STYLES_FLEX_ROW_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportRohcAreaManagementComponent implements OnInit {
  areaGridDefinition: GridDefinition;
  officeGridDefinition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              public form: ReportRohcDetailsFormState,
              private commands: ReportRohcDetailsCommandService,
              private business: ReportRohcDetailsBusinessService,
              private gridDefinitionFactory: GridDefinitionFactory) {
  }

  ngOnInit(): void {
    this.areaGridDefinition = this.buildAreaGridDefinition();
    this.officeGridDefinition = this.buildOfficeGridDefinition();
  }

  onAreaCreate(): void {
    this.commands.createArea();
  }

  onAreaOpen(area: ReportRohcArea): void {
    this.commands.openArea(area);
  }

  onAreaDelete(area: ReportRohcArea): void {
    this.commands.deleteArea(area);
  }

  onSelectedAreaChange(id: ID): void {
    this.commands.selectArea(id);
  }

  onOfficeFocusedCellChange(): void {
    this.commands.resetOfficeFocusedCell();
  }

  onOfficeAdd(): void {
    this.commands.addOffice();
  }

  onOfficeRemove(office: ReportRohcOffice): void {
    this.commands.removeOffice(office);
  }

  onLayoutChange(layout: GridLayout): void {
    this.commands.setGridLayout('area', layout);
  }

  private buildAreaGridDefinition(): GridDefinition {
    const model = createDefaultGridModel({ getId: getReportRohcAreaId });
    return this.gridDefinitionFactory.buildManualWithModel({
      model,
      columns: [
        { field: 'name', headerName: this.translations.get('lbl_name'), width: 150, suppressSizeToFit: true },
        { field: 'description', headerName: this.translations.get('lbl_description'), width: 150 },
      ],
      options: {
        rowSelection: 'single',
        rowClassRules: {
          [ROW_DIRTY_CLASS]: ({ data }: { data: ReportRohcArea }) => whenModified(data),
        },
      },
    });
  }

  private buildOfficeGridDefinition(): GridDefinition {
    const { officeGridLayout } = getRouteInitData(this.route);

    const model = createDefaultGridModel({
      getId: getReportRohcOfficeId,
      getCellDef: reportRohcOfficeFieldState,
      validate: validateReportRohcOfficeField,
      command: (entity, field, value) => {
        if (field === 'housePlan') {
          this.commands.setOfficeHousePlan(entity, value);
        }
      },
    });
    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      layout: officeGridLayout,
      model,
      columns: {
        housePlan: buildEditableAutocompleteColumn(
          {
            nameProperty: 'name',
            loadMore: (event, { data }) =>
              event.load(this.business.findAllHousePlansByQuery(getReportRohcOfficeCatalogId(data), event.query)),
          },
          model.getCellAccessor('housePlan')),
      },
      options: {
        rowClassRules: {
          [ROW_DIRTY_CLASS]: ({ data }: { data: ReportRohcOffice }) => whenModified(data),
        },
      },
    });
  }
}
